import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import * as R from 'ramda';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { filter, first, map, shareReplay, switchMap, tap } from 'rxjs/operators';

import { APIService, KPI } from '../api';
import { SELECTED_SCENARII_LOCAL_STORAGE_KEY } from './constants';
import { StyleService } from './style.service';


@Injectable()
export class InvestmentScenariiService {

  private scenariiRefresh$ = new BehaviorSubject<void>(undefined);
  private _scenarii$: Observable<KPI.InvestmentScenario[]>;

  constructor(
    private api: APIService,
    private translate: TranslateService,
    private styleService: StyleService,
  ) { }

  public get scenarii$(): Observable<KPI.InvestmentScenario[]> {
    if (!this._scenarii$) {
      this._scenarii$ = this.scenariiRefresh$.pipe(
        switchMap(() => this.api.investments_investment_scenarii()),
        tap((scenarii) =>
          scenarii.filter((scenario) => scenario.color).forEach((scenario) => {
            this.styleService.setStyle(
              `.scenario-${scenario.id}`,
              'backgroundColor',
              // Set transparency to 0.5, in order to match the color in charts which seems more transparent
              StyleService.hexToRGB(scenario.color, 0.5),
            );
          }),
        ),
        map((scenarii) =>
          R.sortWith(
            [R.descend(R.prop('is_main')), R.ascend(R.prop('id'))],
            scenarii,
          ),
        ),
        shareReplay(1)
      );
    }
    return this._scenarii$;
  }

  public refreshScenarii(): void {
    this.scenariiRefresh$.next();
  }

  public get mainScenario$(): Observable<KPI.InvestmentScenario> {
    return this.scenarii$.pipe(
      filter((scenarii) => scenarii.length > 0),
      map((scenarii) => scenarii.find((scenario) => scenario.is_main)),
    );
  }

  public scenarioById$(id: number): Observable<KPI.InvestmentScenario> {
    return this.scenarii$.pipe(
      filter((scenarii) => scenarii.length > 0),
      map((scenarii) => scenarii.find((scenario) => scenario.id === id)),
    );
  }

  public createScenario(): Observable<KPI.InvestmentScenario> {
    return this.scenarii$.pipe(
      first(),
      switchMap((scenarii) => {
        return this.translate.get(
          'core.utils.investment-scenarii.default_scenario_name',
          {
            index: scenarii.length + 1,
          },
        );
      }),
      switchMap((scenarioName) => {
        return this.api.investments_investment_scenarii_post({
          name: scenarioName,
        });
      }),
      tap((newScenario) => {
        this.refreshScenarii();
        const currentRight = this._selectedScenarii['right'].value;
        if (!currentRight) {
          this.setSelectedScenario(newScenario.id, 'right');
        }
      }),
    );
  }

  public updateScenario(
    id: number,
    field: string,
    value: any,
  ): Observable<KPI.InvestmentScenario> {
    return this.api
      .investments_investment_scenarii_patch(id, { [field]: value })
      .pipe(tap(() => this.refreshScenarii()));
  }

  public deleteScenario(id: number): Observable<void> {
    return this.api
      .investments_investment_scenarii_delete(id)
      .pipe(map(() => this.refreshScenarii()));
  }

  public makeScenarioMain(id: number): Observable<KPI.InvestmentScenario> {
    return this.api
      .investments_investment_scenarii_patch(id, { is_main: true })
      .pipe(tap(() => this.refreshScenarii()));
  }

  public _flaggedScenarioChanged$ = new BehaviorSubject<void>(undefined);
  public flagScenario(id: number): Observable<any> {
    return this.api.investments_investment_reset_scenarii(id)
      .pipe(tap(() => this.refreshScenarii()));
  }

  private _selectedScenarii: { [key in KPI.LeftRight]: BehaviorSubject<number> } = {
    left: new BehaviorSubject<number>(undefined),
    right: new BehaviorSubject<number>(undefined),
  };
  public getSelectedScenario(side: KPI.LeftRight): Observable<KPI.InvestmentScenario> {
    this.loadSelectedScenarii();
    return combineLatest([
      this.scenarii$,
      this._selectedScenarii[side]
    ]).pipe((
      map(([scenarii, scenarioId]) => {
        if (!scenarioId) {
          if (side === 'left') {
            return scenarii[0];
          }
          return null;
        }
        return scenarii.find(scenario => scenario.id === scenarioId);
      })
    ));
  }

  public get selectedScenarii$(): Observable<KPI.InvestmentScenario[]> {
    return combineLatest([
      this.getSelectedScenario('left'),
      this.getSelectedScenario('right'),
    ]);
  }

  public setSelectedScenario(scenarioId: number | null, side: KPI.LeftRight) {
    const scenarii = localStorage.getItem(SELECTED_SCENARII_LOCAL_STORAGE_KEY);
    let scenariiParsed: { [key in KPI.LeftRight]?: number } = {};
    if (scenarii) {
      scenariiParsed = JSON.parse(scenarii);
    }

    if (scenarioId) {
      scenariiParsed[side] = scenarioId;
      this._selectedScenarii[side].next(scenarioId);
    } else {
      delete scenariiParsed[side];
      this._selectedScenarii[side].next(null);
    }

    localStorage.setItem(
      SELECTED_SCENARII_LOCAL_STORAGE_KEY,
      JSON.stringify(scenariiParsed),
    );
  }

  private _selectedScenariiLoaded: boolean = false;
  private loadSelectedScenarii(): void {
    if (!this._selectedScenariiLoaded) {
      const scenarii = localStorage.getItem(SELECTED_SCENARII_LOCAL_STORAGE_KEY);
      if (scenarii) {
        const scenariiParsed: { [key in KPI.LeftRight]?: number } = JSON.parse(scenarii);
        Object.keys(scenariiParsed).forEach((side) => {
          this._selectedScenarii[side].next(scenariiParsed[side]);
        });
      }
      this._selectedScenariiLoaded = true;
    }
  }

  private investmentLinks: Observable<KPI.InvestmentLink[]>;
  public getInvestmentLinks(): Observable<KPI.InvestmentLink[]> {
    if (!this.investmentLinks) {
      this.investmentLinks = this.api.get_investment_links().pipe(shareReplay(1));
    }
    return this.investmentLinks;
  }
}
